import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Box, Typography, Container, CircularProgress, Alert, Button, IconButton,
  Card, CardContent, Grid, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import BatchResultsTable, { StatusBadge, ScoreChange } from '../components/BatchResultsTable';
import { fetchBatchFiles, fetchBatchIndex, getPresignedPdfUrl } from '../utils/batchResultsService';
import { Authority } from '../utilities/constants';
import CustomCredentialsProvider from '../utilities/CustomCredentialsProvider';

function formatFileSize(bytes) {
  if (!bytes) return '-';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function ScoreBar({ value, max = 100, color }) {
  if (value == null) return <Typography variant="body2" color="text.secondary">-</Typography>;
  const width = Math.min((value / max) * 100, 100);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: '#e2e8f0', minWidth: 60 }}>
        <Box sx={{ height: '100%', borderRadius: 4, backgroundColor: color || '#3b82f6', width: `${width}%`, transition: 'width 0.3s' }} />
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 40, textAlign: 'right' }}>
        {value}%
      </Typography>
    </Box>
  );
}

function SummaryCard({ value, label, color, variant }) {
  const bgMap = {
    accent: 'linear-gradient(135deg, #ecfdf5, #f0fdf4)',
    error: 'linear-gradient(135deg, #fef2f2, #fff1f2)',
  };
  const colorMap = { accent: '#059669', error: '#dc2626' };
  return (
    <Card elevation={0} sx={{
      border: '1px solid #e2e8f0', borderRadius: 3,
      background: variant ? bgMap[variant] : '#fff', textAlign: 'center',
    }}>
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: color || (variant ? colorMap[variant] : '#1e40af'), lineHeight: 1.2 }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function BatchDetailPage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [files, setFiles] = useState([]);
  const [batchMeta, setBatchMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [awsCredentials, setAwsCredentials] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [generatedAt, setGeneratedAt] = useState(null);

  useEffect(() => {
    if (!auth.isAuthenticated) return;
    (async () => {
      try {
        const provider = new CustomCredentialsProvider();
        provider.loadFederatedLogin({ domain: Authority, token: auth.user?.id_token });
        const { credentials: c } = await provider.getCredentialsAndIdentityId();
        setAwsCredentials({
          accessKeyId: c.accessKeyId,
          secretAccessKey: c.secretAccessKey,
          sessionToken: c.sessionToken,
        });
      } catch (err) {
        setError('Failed to obtain AWS credentials.');
      }
    })();
  }, [auth.isAuthenticated, auth.user]);

  const loadData = useCallback(async () => {
    if (!awsCredentials || !batchId) return;
    setLoading(true);
    setError(null);
    try {
      const [filesResult, indexResult] = await Promise.all([
        fetchBatchFiles(awsCredentials, batchId),
        fetchBatchIndex(awsCredentials),
      ]);
      const meta = indexResult.batches.find(b => b.batch_id === batchId);
      setBatchMeta(meta || null);
      setFiles(filesResult.files.map(f => ({ ...f, _key: f.file_id })));
      setGeneratedAt(filesResult.generated_at || indexResult.generated_at);
    } catch (err) {
      console.error('Error loading batch detail:', err);
      setError('Failed to load batch details. The data may not have been exported for this batch.');
    } finally {
      setLoading(false);
    }
  }, [awsCredentials, batchId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDownload = async (file) => {
    if (!file.s3_output_key || !file.s3_bucket) return;
    setDownloading(prev => ({ ...prev, [file.file_id]: true }));
    try {
      const url = await getPresignedPdfUrl(
        awsCredentials,
        file.s3_bucket,
        file.s3_output_key,
        `COMPLIANT_${file.filename}`
      );
      window.open(url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(prev => ({ ...prev, [file.file_id]: false }));
    }
  };

  const completedFiles = files.filter(f => f.status === 'COMPLETED');
  const failedFiles = files.filter(f => f.status === 'FAILED' || f.status === 'TIMED_OUT');
  const avgBefore = completedFiles.length > 0
    ? Math.round(completedFiles.reduce((s, f) => s + (f.pdf_score_before || 0), 0) / completedFiles.length * 10) / 10
    : null;
  const avgAfter = completedFiles.length > 0
    ? Math.round(completedFiles.reduce((s, f) => s + (f.pdf_score_after || 0), 0) / completedFiles.length * 10) / 10
    : null;
  const perfectCount = completedFiles.filter(f => f.pdf_score_after === 100).length;

  const completedColumns = [
    { id: 'filename', label: 'Filename', sortable: true },
    { id: 'department', label: 'Dept', sortable: true,
      render: (val) => <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{val || '-'}</Typography>,
    },
    { id: 'file_size_bytes', label: 'Size', sortable: true, align: 'right',
      render: (val) => formatFileSize(val),
    },
    { id: 'original_path', label: 'Original Path', sortable: true,
      render: (val) => (
        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#64748b', wordBreak: 'break-all' }}>
          {val || '-'}
        </Typography>
      ),
    },
    { id: 'pdf_score_before', label: 'Score Before', sortable: true, align: 'center',
      render: (val) => <ScoreBar value={val} color="#94a3b8" />,
    },
    { id: 'pdf_score_after', label: 'Score After', sortable: true, align: 'center',
      render: (val) => <ScoreBar value={val} color="#059669" />,
    },
    { id: '_change', label: 'Change', sortable: false, align: 'center',
      render: (_, row) => <ScoreChange before={row.pdf_score_before} after={row.pdf_score_after} />,
    },
    { id: '_download', label: '', sortable: false, align: 'center',
      render: (_, row) => (
        <Tooltip title="Download remediated PDF">
          <IconButton
            size="small"
            onClick={() => handleDownload(row)}
            disabled={downloading[row.file_id]}
            sx={{ color: '#3b82f6' }}
          >
            {downloading[row.file_id] ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const failedColumns = [
    { id: 'filename', label: 'Filename', sortable: true },
    { id: 'department', label: 'Dept', sortable: true,
      render: (val) => <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{val || '-'}</Typography>,
    },
    { id: 'file_size_bytes', label: 'Size', sortable: true, align: 'right',
      render: (val) => formatFileSize(val),
    },
    { id: 'original_path', label: 'Original Path', sortable: true,
      render: (val) => (
        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#64748b', wordBreak: 'break-all' }}>
          {val || '-'}
        </Typography>
      ),
    },
    { id: 'status', label: 'Status', sortable: true, align: 'center',
      render: (val) => <StatusBadge status={val} />,
    },
    { id: 'error_message', label: 'Error', sortable: false,
      render: (val) => (
        <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.8rem', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {val || '-'}
        </Typography>
      ),
    },
    { id: '_download', label: '', sortable: false, align: 'center',
      render: (_, row) => {
        if (!row.s3_output_key || !row.s3_bucket) return null;
        return (
          <Tooltip title="Download remediated PDF (output exists despite error status)">
            <IconButton
              size="small"
              onClick={() => handleDownload(row)}
              disabled={downloading[row.file_id]}
              sx={{ color: '#f59e0b' }}
            >
              {downloading[row.file_id] ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const dept = batchMeta?.department || 'Unknown';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 50%, #3b82f6 100%)',
        color: '#fff', py: 4, px: 3,
      }}>
        <Container maxWidth="xl">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/batch-results')}
            sx={{ color: '#fff', opacity: 0.8, mb: 1, '&:hover': { opacity: 1 } }}
          >
            All Batches
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
            {dept} Department
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
            {batchMeta?.batch_group && (
              <Typography variant="body2" sx={{
                opacity: 0.9, backgroundColor: 'rgba(255,255,255,0.2)',
                px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
              }}>
                Batch {batchMeta.batch_group}
              </Typography>
            )}
            <Typography variant="body2" sx={{ opacity: 0.7, fontFamily: 'monospace' }}>
              {batchId}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: -2, position: 'relative', zIndex: 1, pb: 6 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <SummaryCard value={files.length} label="Total Files" />
              </Grid>
              {avgBefore != null && (
                <Grid size={{ xs: 6, sm: 3 }}>
                  <SummaryCard value={`${avgBefore}%`} label="Avg Before" />
                </Grid>
              )}
              {avgAfter != null && (
                <Grid size={{ xs: 6, sm: 3 }}>
                  <SummaryCard value={`${avgAfter}%`} label="Avg After" variant="accent" />
                </Grid>
              )}
              {perfectCount > 0 && (
                <Grid size={{ xs: 6, sm: 3 }}>
                  <SummaryCard value={perfectCount} label="Perfect (100%)" variant="accent" />
                </Grid>
              )}
              {failedFiles.length > 0 && (
                <Grid size={{ xs: 6, sm: 3 }}>
                  <SummaryCard value={failedFiles.length} label="Failures" variant="error" />
                </Grid>
              )}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button size="small" startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
            </Box>

            {/* Completed Files */}
            {completedFiles.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Completed Files</Typography>
                  <Typography variant="caption" sx={{
                    backgroundColor: '#059669', color: '#fff', px: 1, py: 0.25,
                    borderRadius: 99, fontWeight: 600,
                  }}>
                    {completedFiles.length}
                  </Typography>
                </Box>
                <BatchResultsTable
                  columns={completedColumns}
                  rows={completedFiles}
                  defaultSort="pdf_score_after"
                  defaultOrder="desc"
                  searchPlaceholder="Search by filename or department..."
                />
              </Box>
            )}

            {/* Failed Files */}
            {failedFiles.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Failures</Typography>
                  <Typography variant="caption" sx={{
                    backgroundColor: '#dc2626', color: '#fff', px: 1, py: 0.25,
                    borderRadius: 99, fontWeight: 600,
                  }}>
                    {failedFiles.length}
                  </Typography>
                </Box>
                <BatchResultsTable
                  columns={failedColumns}
                  rows={failedFiles}
                  defaultSort="filename"
                  searchPlaceholder="Search failures..."
                />
              </Box>
            )}
          </>
        )}

        <Box sx={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem', mt: 4, pt: 2, borderTop: '1px solid #e2e8f0' }}>
          ADA PDF Remediation Tool &middot; Miami-Dade County CITD
          {generatedAt && (
            <Box sx={{ mt: 0.5 }}>
              Data generated: {new Date(generatedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}
